import {
  toWSDateString,
  WSButton,
  WSElement,
  WSFlexBox,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { IPayerResponse } from "@wingspanhq/payments/dist/interfaces";
import { Invoice, InvoicePaymentMethod } from "@wingspanhq/wingspan-api-sdk";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import { Card } from "../../modules/PaymentApp/components/Card/Card";
import { getPayerName } from "../../modules/Payers/utils/getPayerNames";
import { BillingAttachmentDownloadLink } from "./BillingAttachmentDownloadLink";
import { InvoiceSplits } from "./InvoiceSplits";
import { InvoiceTotals } from "./InvoiceTotals/InvoiceTotals";
import { InvoiceWorkLog } from "./InvoiceWorkLog";

export const paymentMethodLabel = (method?: InvoicePaymentMethod): string => {
  switch (method) {
    case InvoicePaymentMethod.ACH:
      return "ACH transfer";
    case InvoicePaymentMethod.CARD:
    case InvoicePaymentMethod.CARD_PUSH:
      return "Card";
    case InvoicePaymentMethod.WIRE:
      return "Wire transfer";
    case InvoicePaymentMethod.RTP:
      return "RTP";
    case InvoicePaymentMethod.FED_NOW:
      return "FedNow";
    case InvoicePaymentMethod.CHECK:
      return "Check";
    case InvoicePaymentMethod.OFF_PLATFORM:
      return "Off platform";
    case InvoicePaymentMethod.BOOK:
      return "Wingspan balance";
    default:
      return "";
  }
};

type Props = { invoice: Invoice; payer: IPayerResponse; is1099Flow?: boolean };

export const InvoiceDetails: React.FC<Props> = ({
  invoice,
  payer,
  is1099Flow
}) => {
  const history = useHistory();
  const [visible, setVisible] = useState(true);

  const payerName = getPayerName(payer);
  const payerEmail = payer.user?.email;

  const metadata = invoice.metadata ?? {};
  const labelKeys = Object.keys(metadata).filter(
    key => key[0] === key[0].toUpperCase()
  );

  const paidWithPayment = invoice.payments?.find(payment => payment.method);
  const attachments = invoice.attachments?.data ?? [];

  return (
    <Card>
      <WSList gap="XL">
        <WSFlexBox wrap="nowrap" justify="space-between">
          <WSButton.Link
            onClick={() => {
              setVisible(prev => !prev);
            }}
            rightIcon={visible ? "caret-up" : "caret-down"}
          >
            {visible ? "Hide" : "Show"} invoice details
          </WSButton.Link>
          <WSText.Heading5>
            {formatMoney(invoice.amount ?? 0, invoice.currency || "USD")}
          </WSText.Heading5>
        </WSFlexBox>

        {visible && (
          <WSList gap="XL">
            <WSElement>
              <WSText.ParagraphSm mb="XS" color="gray500">
                Bill to
              </WSText.ParagraphSm>
              {payerName !== payerEmail && <WSText>{payerName}</WSText>}
              <WSText.ParagraphSm
                color="blue400"
                weight="medium"
                onClick={() => {
                  history.push({
                    pathname: `/member/payers/${payer.payerId}`
                  });
                }}
              >
                {[
                  payerEmail,
                  ...(payer.payeeOwnedData.payerEmailCC || [])
                ].join(", ")}
              </WSText.ParagraphSm>
            </WSElement>

            {metadata.purchaseOrderNumber && (
              <WSElement>
                <WSText.ParagraphSm mb="XS" color="gray500">
                  PO Number
                </WSText.ParagraphSm>
                <WSText>{metadata.purchaseOrderNumber}</WSText>
              </WSElement>
            )}

            <WSElement>
              <WSText.ParagraphSm mb="XS" color="gray500">
                Due date
              </WSText.ParagraphSm>
              <WSText>{toWSDateString(invoice.dueDate, "monthDayYear")}</WSText>
            </WSElement>

            {metadata.projectName && (
              <WSElement>
                <WSText.ParagraphSm mb="XS" color="gray500">
                  Project name
                </WSText.ParagraphSm>
                <WSText>{metadata.projectName}</WSText>
              </WSElement>
            )}

            {invoice.notes && (
              <WSElement>
                <WSText.ParagraphSm mb="XS" color="gray500">
                  Invoice notes
                </WSText.ParagraphSm>
                <WSText>{invoice.notes}</WSText>
              </WSElement>
            )}

            {labelKeys.map(key => (
              <WSElement key={key}>
                <WSText.ParagraphSm mb="XS" color="gray500">
                  {key.replace(/([a-z])([A-Z])/g, "$1 $2")}
                </WSText.ParagraphSm>
                <WSText>{metadata[key]}</WSText>
              </WSElement>
            ))}

            {paidWithPayment && (
              <WSElement>
                <WSText.ParagraphSm mb="XS" color="gray500">
                  Paid with
                </WSText.ParagraphSm>
                <WSText>
                  {paymentMethodLabel(paidWithPayment.method)}
                  {paidWithPayment.sourceMetadata?.institution
                    ? ` • ${paidWithPayment.sourceMetadata.institution}`
                    : ""}
                  {paidWithPayment.sourceMetadata?.mask
                    ? ` (${paidWithPayment.sourceMetadata.mask})`
                    : ""}
                </WSText>
              </WSElement>
            )}

            {invoice.events?.markedPaidAt ? (
              <WSElement>
                <WSText.ParagraphSm mb="XS" color="gray500">
                  Paid date
                </WSText.ParagraphSm>
                <WSText>
                  {toWSDateString(invoice.events.markedPaidAt, "monthDayYear")}
                </WSText>
              </WSElement>
            ) : invoice.events?.instantPayoutAt ? (
              <WSElement>
                <WSText.ParagraphSm mb="XS" color="gray500">
                  Instant payout date
                </WSText.ParagraphSm>
                <WSText>
                  {toWSDateString(
                    invoice.events.instantPayoutAt,
                    "monthDayYear"
                  )}
                </WSText>
              </WSElement>
            ) : invoice.events?.depositedAt ? (
              <WSElement>
                <WSText.ParagraphSm mb="XS" color="gray500">
                  Payout initiated date
                </WSText.ParagraphSm>
                <WSText>
                  {toWSDateString(invoice.events.depositedAt, "monthDayYear")}
                </WSText>
                <WSText color="gray500" mt="XS">
                  Funds may take 1–2 business days to arrive.
                </WSText>
              </WSElement>
            ) : invoice.events?.estimatedDepositAt ? (
              <WSElement>
                <WSText.ParagraphSm mb="XS" color="gray500">
                  Payout initiation date
                </WSText.ParagraphSm>
                <WSText>
                  {toWSDateString(
                    invoice.events.estimatedDepositAt,
                    "monthDate"
                  )}
                </WSText>
                <WSText color="gray500" mt="XS">
                  Funds may take 1–2 business days to arrive.
                </WSText>
              </WSElement>
            ) : null}

            <InvoiceWorkLog invoice={invoice} />

            <InvoiceTotals invoice={invoice} is1099Flow={is1099Flow} />

            {invoice.lateFeeHandling &&
              (!!invoice.lateFeeHandling.lateFeePercentage ||
                !!invoice.lateFeeHandling.lateFeeAmount) && (
                <WSElement>
                  <WSText.ParagraphSm mb="XS" color="gray500">
                    Late fee
                  </WSText.ParagraphSm>
                  <WSText>
                    {invoice.lateFeeHandling.lateFeePercentage
                      ? `${invoice.lateFeeHandling.lateFeePercentage}%`
                      : formatMoney(
                          invoice.lateFeeHandling.lateFeeAmount!,
                          invoice.currency || "USD"
                        )}{" "}
                    every {invoice.lateFeeHandling.frequency?.every}{" "}
                    {invoice.lateFeeHandling.frequency?.interval} overdue
                  </WSText>
                </WSElement>
              )}

            {invoice.splits && invoice.splits.length > 0 && (
              <WSElement>
                <WSText.Heading5 mb="M">Splits</WSText.Heading5>
                <InvoiceSplits invoice={invoice} />
              </WSElement>
            )}

            {attachments.length > 0 && (
              <WSElement>
                <WSText.ParagraphSm mb="XS" color="gray500">
                  Attachments
                </WSText.ParagraphSm>
                {attachments.map(attachment =>
                  attachment.memberFileId ? (
                    <BillingAttachmentDownloadLink
                      key={attachment.id}
                      fileId={attachment.memberFileId}
                      mb="M"
                    />
                  ) : null
                )}
              </WSElement>
            )}
          </WSList>
        )}
      </WSList>
    </Card>
  );
};
